/**
 * Admin V3 — Corporate / Entreprises — Table component
 *
 * 11 columns: (expand) | Entreprise | Contact | SIRET | Statut
 *           | Délai paiement | Budget mensuel | Courses | Total dépensé
 *           | Créé le | Actions.
 *
 * Per-row expandable detail panel shows:
 *   - address, billing_email, billing_address
 *   - base_rate (€/km), negotiated_discount (% formatted)
 *
 * Row Actions (AV3 A-6, RBAC-gated): Edit · Activate/Suspend · Employees.
 *
 * Suspended/inactive rows: rose left-border (suspended), slate (inactive).
 */

import React, { useState } from 'react';
import { Building2, AlertCircle, RotateCcw, ChevronRight, ChevronDown, Pencil, Users } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import usePermissions from '../../shared/hooks/usePermissions';
import CorporateStatusToggle from './CorporateStatusToggle';
import {
  cn,
  currency,
  EmptyState,
  SmallPill,
  enterpriseStatusMeta,
} from '../../shared/visualTokens';
import { t } from '../../i18n';

const formatDate = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

// Format SIRET (14 digits) as: "XXX XXX XXX XXXXX" for readability.
const formatSiret = (s) => {
  if (!s) return '—';
  const clean = String(s).replace(/\s+/g, '');
  if (clean.length === 14) {
    return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6, 9)} ${clean.slice(9, 14)}`;
  }
  return s;
};

// Format discount as percentage (backend stores as fraction 0-1).
const formatDiscount = (d) => {
  if (d == null) return '—';
  const num = Number(d);
  if (!Number.isFinite(num)) return '—';
  // Heuristic: values <= 1 are treated as fractions (0.15 → 15%), values >1 as already %
  const pct = num <= 1 ? num * 100 : num;
  return `${pct.toFixed(1)}%`;
};

const initialsOf = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';

const SkeletonRows = () => (
  <>
    {Array(5).fill(0).map((_, i) => (
      <tr key={`sk-${i}`} className="border-b border-slate-100">
        {Array(11).fill(0).map((_, j) => (
          <td key={j} className="px-4 py-3.5">
            <div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const BudgetUsageBar = ({ spent, budget, locale }) => {
  if (budget == null || Number(budget) <= 0) {
    return (
      <div>
        <div className="text-slate-700 font-medium tabular-nums">{currency(spent || 0)}</div>
        <div className="text-[10px] text-slate-400 italic">{t('corporate.budget.unlimited', locale)}</div>
      </div>
    );
  }
  const safeBudget = Number(budget);
  const safeSpent = Math.max(0, Number(spent) || 0);
  const pct = Math.min(100, (safeSpent / safeBudget) * 100);
  const tone =
    pct >= 90 ? 'bg-rose-500' :
    pct >= 75 ? 'bg-amber-500' :
                'bg-emerald-500';
  return (
    <div className="min-w-[120px]">
      <div className="text-[12px] text-slate-700 tabular-nums">
        {currency(safeSpent)} / {currency(safeBudget)}
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={cn('h-full', tone)} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-[10px] text-slate-500 tabular-nums w-9 text-right">{pct.toFixed(0)}%</span>
      </div>
    </div>
  );
};

const DetailPanel = ({ enterprise, locale }) => {
  const rows = [
    enterprise.address          && [t('corporate.detail.address',         locale), enterprise.address],
    enterprise.billing_email    && [t('corporate.detail.billingEmail',    locale), enterprise.billing_email],
    enterprise.billing_address  && [t('corporate.detail.billingAddress',  locale), enterprise.billing_address],
    enterprise.base_rate != null && [t('corporate.detail.baseRate',       locale), `${Number(enterprise.base_rate).toFixed(2)} €/km`],
    enterprise.negotiated_discount != null && [t('corporate.detail.negotiatedDiscount', locale), formatDiscount(enterprise.negotiated_discount)],
  ].filter(Boolean);

  if (rows.length === 0) {
    return <div className="text-[11px] italic text-slate-400 px-2 py-1">—</div>;
  }

  return (
    <div className="bg-slate-50/60 border border-slate-200 rounded-xl p-4 my-2">
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="text-[12px]">
            <dt className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">{label}</dt>
            <dd className="text-slate-700 break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

const CorporateListTable = ({ enterprises, loading, error, isFilterActive, onRetry, onEdit, onManageEmployees, onRefresh, locale = 'fr' }) => {
  const { can } = usePermissions();
  const [expanded, setExpanded] = useState({});

  const toggleRow = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const errorBanner = error ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-corporate-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('corporate.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline" size="sm" onClick={onRetry}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-corporate-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('corporate.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  const emptyContent = (
    <EmptyState
      icon={Building2}
      title={t('corporate.empty.title', locale)}
      desc={t(isFilterActive ? 'corporate.empty.descFilter' : 'corporate.empty.descNoFilter', locale)}
    />
  );

  return (
    <>
      {errorBanner}
      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-2 py-3 w-8" />
              {[
                t('corporate.col.enterprise',    locale),
                t('corporate.col.contact',       locale),
                t('corporate.col.siret',         locale),
                t('corporate.col.status',        locale),
                t('corporate.col.paymentTerms', locale),
                t('corporate.col.budget',        locale),
                t('corporate.col.totalBookings', locale),
                t('corporate.col.totalSpent',    locale),
                t('corporate.col.created',       locale),
                t('corporate.col.actions',       locale),
              ].map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows />
            ) : enterprises.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-4 py-8">{emptyContent}</td>
              </tr>
            ) : (
              enterprises.map((e) => {
                const key = e.id;
                const isExpanded = Boolean(expanded[key]);
                const statusKey = e.status || 'active';
                const statusMeta = enterpriseStatusMeta[statusKey] || {
                  label: statusKey,
                  pill: 'bg-slate-100 text-slate-500 border border-slate-200',
                };
                const isSuspended = statusKey === 'suspended';

                return (
                  <React.Fragment key={key}>
                    <tr
                      className={cn(
                        'border-b border-slate-100 hover:bg-slate-50/70 transition-colors',
                        isSuspended && 'bg-rose-50/30'
                      )}
                      style={isSuspended ? { boxShadow: 'inset 3px 0 0 #ef4444' } : undefined}
                    >
                      <td className="px-2 py-3.5 align-top">
                        <button
                          type="button"
                          onClick={() => toggleRow(key)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          title={isExpanded ? t('corporate.detail.hide', locale) : t('corporate.detail.show', locale)}
                          data-testid={`adminv3-corporate-expand-${key}`}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>

                      {/* Entreprise */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-indigo-300 flex-shrink-0">
                            {initialsOf(e.name)}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-slate-900 truncate">{e.name || '—'}</div>
                            <div className="text-[11px] text-slate-400 truncate">{e.email || ''}</div>
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="px-4 py-3.5">
                        <div className="text-slate-700 text-[13px]">{e.contact_name || '—'}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{e.phone || ''}</div>
                      </td>

                      {/* SIRET */}
                      <td className="px-4 py-3.5 text-slate-500 font-mono text-[12px]">
                        {formatSiret(e.siret)}
                      </td>

                      {/* Statut */}
                      <td className="px-4 py-3.5">
                        <SmallPill meta={statusMeta} />
                      </td>

                      {/* Délai paiement */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {t('corporate.paymentTerms.days', locale).replace('{n}', String(e.payment_terms ?? 30))}
                      </td>

                      {/* Budget mensuel */}
                      <td className="px-4 py-3.5">
                        <BudgetUsageBar spent={e.current_month_spending} budget={e.max_monthly_budget} locale={locale} />
                      </td>

                      {/* Courses */}
                      <td className="px-4 py-3.5 text-slate-700 font-medium tabular-nums">
                        {e.total_bookings ?? 0}
                      </td>

                      {/* Total dépensé */}
                      <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap">
                        {currency(e.total_spent ?? 0)}
                      </td>

                      {/* Créé le */}
                      <td className="px-4 py-3.5 text-slate-500 text-[12px] whitespace-nowrap">
                        {formatDate(e.created_at, locale)}
                      </td>

                      {/* Actions (RBAC-gated) */}
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1">
                          {can('enterprise:update') && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px]" onClick={() => onEdit && onEdit(e)} data-testid={`adminv3-corporate-edit-${key}`}>
                              <Pencil className="h-3.5 w-3.5 mr-1.5" />{t('corporate.action.edit', locale)}
                            </Button>
                          )}
                          {can('enterprise:update') && (
                            <CorporateStatusToggle enterprise={e} onDone={onRefresh} locale={locale} />
                          )}
                          {can('employee:list') && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px]" onClick={() => onManageEmployees && onManageEmployees(e)} data-testid={`adminv3-corporate-employees-${key}`}>
                              <Users className="h-3.5 w-3.5 mr-1.5" />{t('corporate.action.employees', locale)}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-slate-100">
                        <td colSpan={11} className="px-4 py-2">
                          <DetailPanel enterprise={e} locale={locale} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default CorporateListTable;
